// Vite entrypoint for every page that puts the engine on screen: the web editor / runtime pages
// (/:username/:scenename, .../play, /scenes/new) and the examples gallery's own pages
// (/examples/<slug>). Reads everything inline from the #polygon-editor data-attributes (no fetch) and
// boots the engine via loadEditor. See PLAN-web-editor-save-load W1/W2 + "The JS contract".
import { loadEditor, parseEntitlements } from "../editorInit";
import { initErrorReporting } from "../errorReporting";
import "../demo.css";

const el = document.getElementById("polygon-editor");
const loading = document.getElementById("loading");
const bar = document.getElementById("loading-bar");
const note = document.getElementById("loading-note");

// A version the server has not prepared since it last started is sealed on the spot when
// the page asks for it — reading ~45MB off the volume and gzipping it, several seconds —
// and none of that moves the bar, because the browser has not been handed a byte to
// download yet. Only the newest version is warmed at boot (SecureWasm.warm!), and scenes
// legitimately stay pinned to old ones forever, so this is a permanent state of affairs
// rather than a bug to chase: say so instead of showing a frozen bar.
//
// The trigger is deliberately a stall, not the version number: the wasm download owns 80%
// of `overall`, so while the server is still preparing, `overall` cannot rise above the
// 0.2 the other loaders contribute. Staying under that after a few seconds means nothing
// has arrived. (A very slow connection can trip it too, which is why the wording says
// "getting ready" rather than naming a cause we cannot actually observe from here.)
const STALL_AFTER_MS = 2500;
const STALL_BELOW = 0.25;

function showStallNote(version: string): void {
	if (!note) return;
	note.textContent = `Getting Polygon ${version} ready — the first load of a version can take a few seconds. Hang tight.`;
	note.hidden = false;
}

if (el) {
	// BEFORE loadEditor: this registers the sink the engine reports into, and the engine starts
	// fetching wasm inside loadEditor — anything that fails during boot would otherwise go unseen.
	// No-op when the layout emitted no DSN.
	initErrorReporting({ release: el.dataset.polygonVersion ?? "" });

	let overall = 0;
	const stallTimer = window.setTimeout(() => {
		if (overall < STALL_BELOW) showStallNote(el.dataset.polygonVersion ?? "");
	}, STALL_AFTER_MS);

	loadEditor({
		domElement: el,
		sceneData: el.dataset.sceneData ?? "",
		sceneName: el.dataset.sceneName ?? "",
		// /examples/<slug> only: the example to build into the empty scene once the editor is up. Absent
		// on every other page, and `undefined` rather than "" so the engine's own "nothing was asked
		// for" check is the one that runs.
		exampleId: el.dataset.exampleId || undefined,
		polygonVersion: el.dataset.polygonVersion ?? "",
		mode: el.dataset.mode === "play" ? "play" : "edit",
		saveUrl: el.dataset.saveUrl, // existing scene (SAVE_SCENE)
		createUrl: el.dataset.createUrl, // new scene / SAVE_SCENE_AS
		scenesNewUrl: el.dataset.scenesNewUrl, // SCENE_NEW
		sceneEditUrl: el.dataset.sceneEditUrl, // OPEN_EDIT (File→Edit)
		dashboardUrl: el.dataset.dashboardUrl, // OPEN_DASHBOARD
		currentUserIsSceneOwner: el.dataset.currentUserIsSceneOwner === "true",
		// File ▸ Export For Web (scenes/_stage.html.erb). Present for the owner of a saved scene only.
		exportUrl: el.dataset.exportUrl,
		currentUserIsAdmin: el.dataset.currentUserIsAdmin === "true",
		// Signed in or not, which the entitlements cannot say: anonymous and signed-in-free are the
		// same grant. Absent reads as false, which is the safe half (it offers a sign-in to someone
		// who is already signed in rather than hiding the account from someone who is not).
		currentUserIsSignedIn: el.dataset.currentUserIsSignedIn === "true",
		// What the server says this visitor may do on the web. Missing or unparseable is the free
		// tier with nothing allowed (parseEntitlements), which is also the engine's own default.
		entitlements: parseEntitlements(el.dataset.entitlements),
		// Admin only: present ⇒ offer the live API relay for this tab, which the engine takes up when
		// the user opens the LLM panel. Both attributes are absent for everyone else
		// (scenes/_stage.html.erb), and the relay then does nothing. The scene uuid is the one the
		// engine already gets; /scenes/new has none.
		liveApiEnabled: el.dataset.liveApiEnabled === "true",
		liveApiChannel: el.dataset.liveApiChannel,
		liveApiSceneUuid: el.dataset.sceneUuid,
		onLoadingProgress(snapshot) {
			overall = snapshot.overall;
			if (bar)
				bar.style.width = `${
					Math.max(0, Math.min(1, snapshot.overall)) * 100
				}%`;
			// Bytes are moving: whatever the server had to prepare, it is prepared.
			if (overall >= STALL_BELOW) {
				window.clearTimeout(stallTimer);
				if (note) note.hidden = true;
			}
			if (snapshot.allDone && loading) {
				window.clearTimeout(stallTimer);
				loading.style.opacity = "0";
				setTimeout(() => loading.remove(), 300);
			}
		},
	});
}
